/**
 * HomeConversational — nouvelle accueil geoVINUM IA.
 *
 * geoVINUM IA ouvre une conversation, propose 3 pistes contextualisées
 * (voyage en cours, carnet, carte…) et accepte du texte libre. Chaque
 * réponse de l'IA est traduite en actions clicables (navigation / carte
 * iframe inline / fiche KMS).
 */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import MapEmbed from "@/components/MapEmbed";
import AppellationFactsCard from "@/components/AppellationFactsCard";
import TastingDraftCard from "@/components/TastingDraftCard";
import LabelPhotoPicker from "@/components/LabelPhotoPicker";
import { toast } from "sonner";
import {
  Send, Loader2, Sparkles, MapPin, Grape, Building2, Compass,
  NotebookPen, Rocket, MessageSquare, ArrowRight, Wine, RotateCcw,
} from "lucide-react";
import { useTypewriter } from "@/hooks/useTypewriter";
import { renderMarkdown } from "@/lib/markdown";

const SESSION_KEY = "gv_ai_session_id";

function getSessionId() {
  try {
    let sid = localStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = (crypto.randomUUID?.() || `s_${Date.now()}_${Math.random().toString(36).slice(2)}`);
      localStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch {
    return `s_${Date.now()}`;
  }
}

const ACTION_ICONS = {
  journey: Rocket,
  appellation: MapPin,
  cepage: Grape,
  producer: Building2,
  map: MapPin,
  nav: Compass,
  chat: MessageSquare,
  tasting: NotebookPen,
};

const TASTE_TXT = {
  fr: { sub: "Votre guide dans l'univers du vin", ph: "Posez une question — ou cliquez une piste…", disc: "L'abus d'alcool est dangereux pour la santé — à consommer avec modération.", think: "geoVINUM IA réfléchit…", ask: "Décrivez-moi le vin que vous avez goûté : cuvée, millésime, domaine, robe, nez, bouche, votre note… Je structure la fiche pour vous.", busy: "geoVINUM IA structure votre fiche…", ready: "Voici votre fiche — corrigez-la si besoin, puis enregistrez-la.", chip: "Noter une dégustation", cancel: "Annuler", placeholder: "Ex : Chablis 2020 Raveneau, citron vert, finale longue, 17/20", err: "Impossible de structurer la fiche" },
  en: { sub: "Your guide to the world of wine", ph: "Ask a question — or pick a suggestion…", disc: "Alcohol abuse is dangerous for your health — drink responsibly.", think: "geoVINUM AI is thinking…", ask: "Describe the wine you tasted: cuvée, vintage, estate, colour, nose, palate, your score… I'll structure the sheet for you.", busy: "geoVINUM AI is structuring your sheet…", ready: "Here is your sheet — edit it if needed, then save it.", chip: "Log a tasting", cancel: "Cancel", placeholder: "E.g. Chablis 2020 Raveneau, lime, long finish, 17/20", err: "Could not structure the sheet" },
  es: { sub: "Tu guía en el universo del vino", ph: "Haz una pregunta — o elige una pista…", disc: "El abuso de alcohol es peligroso para la salud — consúmelo con moderación.", think: "geoVINUM IA está pensando…", ask: "Descríbeme el vino que probaste: cuvée, añada, bodega, color, nariz, boca, tu nota… Yo estructuro la ficha.", busy: "geoVINUM IA está estructurando tu ficha…", ready: "Aquí tienes tu ficha: corrígela si hace falta y guárdala.", chip: "Anotar una cata", cancel: "Cancelar", placeholder: "Ej.: Chablis 2020 Raveneau, lima, final largo, 17/20", err: "No se pudo estructurar la ficha" },
  pt: { sub: "O teu guia no universo do vinho", ph: "Faz uma pergunta — ou escolhe uma pista…", disc: "O abuso de álcool é prejudicial à saúde — consuma com moderação.", think: "A geoVINUM IA está a pensar…", ask: "Descreve-me o vinho que provaste: cuvée, colheita, produtor, cor, nariz, boca, a tua nota… Eu estruturo a ficha.", busy: "A geoVINUM IA está a estruturar a tua ficha…", ready: "Aqui está a tua ficha — corrige-a se necessário e guarda-a.", chip: "Registar uma prova", cancel: "Cancelar", placeholder: "Ex.: Chablis 2020 Raveneau, lima, final longo, 17/20", err: "Não foi possível estruturar a ficha" },
};

export default function HomeConversational() {
  const { user } = useAuth();
  const { lang } = useT();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]); // {role, text, actions?, mapSlug?}
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tastingMode, setTastingMode] = useState(false);
  const [draftBusy, setDraftBusy] = useState(false);
  const TT = TASTE_TXT[lang] || TASTE_TXT.fr;
  const scrollRef = useRef(null);
  const sessionId = useRef(getSessionId()).current;

  // Load persisted history + welcome message on mount
  useEffect(() => {
    let cancelled = false;
    async function boot() {
      setLoading(true);
      try {
        const hist = await api.get(`/geovinum-ai/history?session_id=${encodeURIComponent(sessionId)}`);
        const past = (hist.data?.messages || []).map((m) => ({
          role: m.role,
          text: m.text,
          actions: m.actions || [],
          __alreadyTyped: true, // never retype historical messages
        }));
        if (past.length > 0) {
          if (!cancelled) setMessages(past);
          return;
        }
        // No history → ask the welcome endpoint
        const w = await api.get(`/geovinum-ai/welcome?lang=${encodeURIComponent(lang || "fr")}`);
        if (!cancelled) setMessages([{ role: "ai", text: w.data.reply, actions: w.data.actions || [] }]);
      } catch {
        if (!cancelled) setMessages([{
          role: "ai",
          text: "Bonjour 👋 Je suis geoVINUM IA. Que souhaitez-vous explorer ?",
          actions: [{ type: "nav", label: "Explorer la carte", href: "/discover" }],
        }]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    boot();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [user?.id]);

  const resetConversation = async () => {
    if (!window.confirm("Effacer notre conversation et repartir à zéro ?")) return;
    try {
      await api.delete(`/geovinum-ai/history?session_id=${encodeURIComponent(sessionId)}`);
    } catch { /* ignore */ }
    try { localStorage.removeItem(SESSION_KEY); } catch { /* ignore */ }
    // regenerate a fresh session id and reload welcome
    window.location.reload();
  };

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  const createTastingDraft = async (notes) => {
    setSending(true);
    setDraftBusy(true);
    try {
      const { data } = await api.post("/tastings/ai-create", { notes, lang, preview: true });
      setMessages((m) => [...m, { role: "ai", text: TT.ready, tastingDraft: data }]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || TT.err);
    } finally { setSending(false); setDraftBusy(false); }
  };

  const startTasting = () => {
    setTastingMode(true);
    setMessages((m) => [...m, { role: "ai", text: TT.ask }]);
  };

  const sendMessage = async (text) => {
    const content = (text ?? input).trim();
    if (!content) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: content }]);
    if (tastingMode) {
      setTastingMode(false);
      await createTastingDraft(content);
      return;
    }
    setSending(true);
    try {
      const r = await api.post("/geovinum-ai/route", { message: content, session_id: sessionId, lang });
      setMessages((m) => [...m, {
        role: "ai",
        text: r.data.reply || "Voici des pistes :",
        actions: r.data.actions || [],
      }]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "geoVINUM IA n'a pas répondu");
      setMessages((m) => [...m, {
        role: "ai",
        text: "Je rencontre un petit souci — essayez le chat classique.",
        actions: [{ type: "chat", label: "Ouvrir le chat", params: { q: content } }],
      }]);
    } finally { setSending(false); }
  };

  const handleAction = (a) => {
    if (a.type === "map") {
      // Inline map reveal
      setMessages((m) => [...m, {
        role: "ai",
        text: `Carte de **${a.params?.appellation || ""}** :`,
        mapSlug: a.params?.appellation,
      }]);
      return;
    }
    if (a.type === "tasting") {
      const notes = (a.params?.notes || "").trim();
      if (notes) createTastingDraft(notes); else startTasting();
      return;
    }
    if (a.type === "chat") {
      navigate(`/chat?q=${encodeURIComponent(a.params?.q || "")}`);
      return;
    }
    if (a.href) navigate(a.href);
  };

  const quickStarters = [
    "Je veux apprendre Chablis",
    "Montre-moi la carte des Côtes du Rhône",
    "Parle-moi du Chardonnay",
    "Que boire ce soir avec un poulet rôti ?",
  ];

  return (
    <div className="flex flex-col h-[calc(100dvh_-_var(--m-header-h)_-_var(--m-tabs-h))] md:h-[100dvh] bg-gradient-to-b from-[#FDF9F3] via-white to-[#F5EDE2]">
      {/* Header */}
      <header className="shrink-0 border-b border-neutral-200 bg-white/90 backdrop-blur">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <img src="/brand/audrey-ai.png" alt="geoVINUM AI"
               className="w-10 h-10 rounded-full ring-2 ring-[#FCE4ED] object-cover" />
          <div className="flex-1 min-w-0">
            <div className="font-display text-base text-[#1F3A5F] leading-tight">geoVINUM IA</div>
            <div className="text-[11px] text-[#8B95A1]">{TT.sub}</div>
          </div>
          <button type="button"
                  onClick={resetConversation}
                  data-testid="home-chat-reset"
                  title="Nouvelle conversation"
                  className="w-9 h-9 rounded-full hover:bg-[#FCE4ED] flex items-center justify-center text-[#8B95A1] hover:text-[#E5276A] transition">
            <RotateCcw className="w-4 h-4" />
          </button>
          <Sparkles className="w-4 h-4 text-[#E5276A]" />
        </div>
      </header>

      {/* Chat scroll */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto" data-testid="home-chat">
        <div className="max-w-3xl mx-auto px-4 py-5 space-y-5 pb-32">
          {loading && (
            <div className="flex gap-3 items-start text-sm text-neutral-400">
              <AiAvatar />
              <div className="pt-2">
                <Loader2 className="w-4 h-4 animate-spin inline-block mr-1" />
                Chargement de notre conversation…
              </div>
            </div>
          )}
          {!loading && messages.map((m, i) => (
            <MessageBubble key={i} msg={m} onAction={handleAction} lang={lang} />
          ))}
          {sending && (
            <div className="flex gap-3 items-start" data-testid="home-chat-loading">
              <AiAvatar />
              <div className="bg-white border border-[#1F3A5F]/10 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm text-sm text-neutral-500 inline-flex items-center gap-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> {draftBusy ? TT.busy : TT.think}
              </div>
            </div>
          )}
          {!loading && messages.length <= 1 && !sending && (
            <div className="flex flex-wrap gap-2 pt-2" data-testid="home-quick-starters">
              {quickStarters.map((q) => (
                <button key={q} onClick={() => sendMessage(q)}
                        className="text-xs px-3 py-1.5 rounded-full bg-white border border-[#1F3A5F]/15 text-[#1F3A5F] hover:border-[#E5276A] hover:text-[#E5276A]">
                  {q}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Composer */}
      <div className="shrink-0 border-t border-neutral-200 bg-white/95 backdrop-blur">
        <div className="max-w-3xl mx-auto px-4 py-3">
          <div className="flex items-center gap-2 mb-2">
            <button type="button" onClick={() => (tastingMode ? setTastingMode(false) : startTasting())}
                    disabled={sending}
                    data-testid="home-chat-tasting-toggle"
                    className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${tastingMode ? "bg-[#E5276A] text-white border-[#E5276A]" : "bg-white text-[#1F3A5F] border-[#1F3A5F]/15 hover:border-[#E5276A] hover:text-[#E5276A]"}`}>
              <NotebookPen className="w-3.5 h-3.5" /> {tastingMode ? TT.cancel : TT.chip}
            </button>
            <LabelPhotoPicker lang={lang} compact testIdPrefix="home-chat-photo"
                              onStart={(img) => { setTastingMode(false); setMessages((m) => [...m, { role: "user", text: "", image: img }]); setSending(true); setDraftBusy(true); }}
                              onResult={(d) => setMessages((m) => [...m, { role: "ai", text: TT.ready, tastingDraft: d }])}
                              onDone={() => { setSending(false); setDraftBusy(false); }} />
          </div>
          <form onSubmit={(e) => { e.preventDefault(); sendMessage(); }}
                className="flex items-center gap-2 bg-white rounded-2xl border border-[#1F3A5F]/15 focus-within:border-[#E5276A] px-3 py-2 shadow-sm">
            <input value={input} onChange={(e) => setInput(e.target.value)}
                   data-testid="home-chat-input"
                   placeholder={tastingMode ? TT.placeholder : TT.ph}
                   className="flex-1 bg-transparent outline-none text-sm text-[#1F3A5F] placeholder:text-[#8B95A1]"
                   autoFocus />
            <Button type="submit" disabled={sending || !input.trim()}
                    data-testid="home-chat-send"
                    className="h-9 bg-[#E5276A] hover:bg-[#C01661] shrink-0">
              <Send className="w-3.5 h-3.5" />
            </Button>
          </form>
          <p className="text-[10px] text-[#8B95A1] text-center mt-2">
            {TT.disc}
          </p>
        </div>
      </div>
    </div>
  );
}

function AiAvatar() {
  return (
    <img src="/brand/audrey-ai.png" alt="AI"
         className="w-9 h-9 rounded-full ring-2 ring-[#FCE4ED] object-cover shrink-0" />
  );
}

function MessageBubble({ msg, onAction, lang }) {
  // Only animate the LATEST AI message when it arrives (not when the component
  // re-mounts via history re-renders). We derive this by diffing props.
  const [typed, setTyped] = useState(() => (msg.role === "ai" ? "" : msg.text));
  useEffect(() => {
    if (msg.role !== "ai") { setTyped(msg.text); return undefined; }
    if (msg.__alreadyTyped) { setTyped(msg.text); return undefined; }
    setTyped("");
    let i = 0;
    const t = setInterval(() => {
      i += 1;
      setTyped(msg.text.slice(0, i));
      if (i >= msg.text.length) { clearInterval(t); msg.__alreadyTyped = true; }
    }, 14);
    return () => clearInterval(t);
  }, [msg]);

  const showActions = msg.role !== "ai" || typed === msg.text;

  if (msg.role === "user") {
    return (
      <div className="flex justify-end" data-testid="msg-user">
        {msg.image ? (
          <img src={msg.image} alt="" data-testid="msg-user-photo" className="max-w-[60%] max-h-64 rounded-2xl rounded-tr-sm object-cover border border-[#1F3A5F]/10" />
        ) : (
          <div className="max-w-[80%] bg-[#1F3A5F] text-white rounded-2xl rounded-tr-sm px-4 py-2.5 text-sm leading-relaxed">
            {msg.text}
          </div>
        )}
      </div>
    );
  }
  return (
    <div className="flex gap-3 items-start" data-testid="msg-ai">
      <AiAvatar />
      <div className="flex-1 min-w-0 space-y-3">
        <div className="bg-white border border-[#1F3A5F]/10 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm">
          <p className="text-sm text-neutral-700 leading-relaxed whitespace-pre-wrap"
             dangerouslySetInnerHTML={{ __html: renderMarkdown(typed) }} />
          {typed !== msg.text && (
            <span className="inline-block w-1.5 h-3.5 bg-[#E5276A] ml-0.5 align-middle animate-pulse" />
          )}
        </div>
        {msg.mapSlug && showActions && (
          <div className="space-y-2" data-testid="msg-ai-map">
            <div className="rounded-2xl overflow-hidden">
              <MapEmbed appellation={msg.mapSlug} height={360} />
            </div>
            <AppellationFactsCard slug={msg.mapSlug} />
          </div>
        )}
        {msg.tastingDraft && showActions && (
          <TastingDraftCard draft={msg.tastingDraft} lang={lang} />
        )}
        {showActions && (msg.actions || []).length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 animate-in fade-in">
            {msg.actions.map((a, i) => {
              const Ic = ACTION_ICONS[a.type] || Compass;
              return (
                <button key={i} onClick={() => onAction(a)}
                        data-testid={`msg-ai-action-${a.type}`}
                        className="group flex items-center gap-3 bg-white hover:bg-[#FBF7F3] border border-[#1F3A5F]/15 hover:border-[#E5276A] rounded-xl px-3 py-2.5 text-left transition-all">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#FCE4ED] to-white flex items-center justify-center shrink-0">
                    <Ic className="w-4 h-4 text-[#E5276A]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-[#1F3A5F] truncate">{a.label}</div>
                  </div>
                  <ArrowRight className="w-3.5 h-3.5 text-[#8B95A1] group-hover:text-[#E5276A] shrink-0" />
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function formatBold(txt) {
  return renderMarkdown(txt);
}
